import React, { FC, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';

const ArticleBody = styled.div`
    padding: 24px;
    overflow-y: auto;
    /* The panel is fixed-width and can't be stretched, so let the whole panel
       content scroll horizontally when something (e.g. a wide table) overflows. */
    overflow-x: auto;
    flex: 1;
    min-width: 0;
    min-height: 0;
    font-size: 14px;
    line-height: 1.6;
    color: #374151;
    overflow-wrap: break-word;
    word-break: break-word;

    h1,
    h2,
    h3,
    h4 {
        margin-top: 24px;
        margin-bottom: 8px;
        color: #111827;
    }

    h1 {
        font-size: 22px;
    }
    h2 {
        font-size: 18px;
    }
    h3 {
        font-size: 16px;
    }

    p {
        margin-bottom: 12px;
    }

    img {
        max-width: 100%;
        height: auto;
        border-radius: 4px;
        margin: 8px 0;
    }

    ul,
    ol {
        padding-left: 24px;
        margin-bottom: 12px;
    }

    li {
        margin-bottom: 4px;
    }

    a {
        color: #3b82f6;
        text-decoration: none;
        &:hover {
            text-decoration: underline;
        }
    }

    /* Zendesk wraps the table in a <figure> with a fixed inline height/width
       (e.g. "height: 1015px; width: 783px") that is shorter than the table
       actually renders in this narrow panel, so the table spills out of the
       wrapper and overlaps the text below it. Neutralise those fixed dimensions
       on whatever element directly wraps a table, plus the wrapper's default
       margin, and the per-row/cell inline heights below. The table keeps its
       natural width and the panel body scrolls horizontally. */
    :has(> table) {
        height: auto !important;
        width: auto !important;
    }

    figure {
        margin: 0 0 16px 0;
    }

    table {
        border-collapse: collapse;
        height: auto !important;
        margin-bottom: 16px;
    }

    tr {
        height: auto !important;
    }

    th,
    td {
        border: 1px solid #e5e7eb;
        padding: 8px 12px;
        text-align: left;
        vertical-align: top;
        height: auto !important;
    }

    th {
        background: #f9fafb;
        font-weight: 600;
    }

    iframe {
        max-width: 100%;
        border: none;
        border-radius: 4px;
        margin: 8px 0;
    }
`;

export const HelpArticleBody: FC<{ articleId: string; body: string }> = ({ articleId, body }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const safeBody = useMemo(() => {
        const document = new DOMParser().parseFromString(body, 'text/html');
        document.querySelectorAll('script, style, object, embed').forEach((element) => element.remove());
        document.querySelectorAll('*').forEach((element) => {
            for (const attribute of Array.from(element.attributes)) {
                const value = attribute.value.trim().toLowerCase();
                if (
                    attribute.name.toLowerCase().startsWith('on') ||
                    ((attribute.name === 'href' || attribute.name === 'src') && value.startsWith('javascript:'))
                )
                    element.removeAttribute(attribute.name);
            }
        });
        return document.body.innerHTML;
    }, [body]);
    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            const anchor = (e.target as HTMLElement).closest('a');
            const href = anchor?.getAttribute('href');
            if (!href) return;
            if (href.startsWith('#') || (href.includes(`/articles/${articleId}`) && href.includes('#'))) {
                e.preventDefault();
                const hash = href.startsWith('#') ? href : `#${href.split('#')[1]}`;
                containerRef.current?.querySelector(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else if (href.startsWith('http')) {
                e.preventDefault();
                window.open(href, '_blank', 'noopener,noreferrer');
            }
        },
        [articleId]
    );

    return <ArticleBody ref={containerRef} onClick={handleClick} dangerouslySetInnerHTML={{ __html: safeBody }} />;
};
